import { CheckCircle2, Package, AlertCircle, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { TruckPostMatchResult } from "@/hooks/useTruckPostMatches";
import type { DatabaseLoad } from "@/lib/loadSearch";

interface Props {
  open: boolean;
  onClose: () => void;
  matchResult: TruckPostMatchResult;
}

function formatAge(postedAt: string | null): string {
  if (!postedAt) return "—";
  const diff = Date.now() - new Date(postedAt).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

function formatRate(rate: number | null): string {
  if (!rate) return "—";
  return `$${rate.toLocaleString()}`;
}

function LoadRow({ load }: { load: DatabaseLoad }) {
  return (
    <div className="grid grid-cols-[32px_1fr_1fr_56px_56px_56px] gap-1.5 items-center py-1.5 px-2 text-[11px] border-b border-border last:border-b-0">
      <span className="text-muted-foreground font-medium">{formatAge(load.posted_at)}</span>
      <span className="truncate font-medium text-foreground">
        {load.origin_city}, {load.origin_state}
      </span>
      <span className="truncate text-muted-foreground">
        → {load.destination_city}, {load.destination_state}
      </span>
      <span className="text-[10px] uppercase text-muted-foreground truncate">
        {(load.equipment_type || "").replace(/_/g, " ")}
      </span>
      <span className="font-semibold" style={{ color: "hsl(var(--brand-teal-deep))" }}>
        {formatRate(load.rate_amount)}
      </span>
      <span className="text-muted-foreground text-[10px]">
        {load.pickup_date || "—"}
      </span>
    </div>
  );
}

export default function PostTruckMatchesAfterPost({ open, onClose, matchResult }: Props) {
  const { status, matches, matchCount, bestRate } = matchResult;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5" style={{ color: "hsl(var(--success))" }} />
            <DialogTitle className="text-base">Truck Posted Successfully</DialogTitle>
          </div>
          <DialogDescription className="text-xs text-muted-foreground">
            Your truck is now visible to brokers on the board.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Loading */}
          {status === "loading" && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span>Finding matching loads…</span>
              </div>
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          )}

          {/* Ready with matches */}
          {status === "ready" && matchCount > 0 && (
            <>
              <div className="flex items-center justify-between px-2 py-2 rounded-lg bg-muted/50 border border-border">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4" style={{ color: "hsl(var(--brand-teal-deep))" }} />
                  <span className="text-xs font-semibold text-foreground">
                    {matchCount} Matching Load{matchCount !== 1 ? "s" : ""} Found
                  </span>
                </div>
                {bestRate !== null && (
                  <span className="text-xs font-bold" style={{ color: "hsl(var(--brand-teal-deep))" }}>
                    Best Rate: {formatRate(bestRate)}
                  </span>
                )}
              </div>

              {/* Column headers */}
              <div className="grid grid-cols-[32px_1fr_1fr_56px_56px_56px] gap-1.5 px-2 text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">
                <span>Age</span>
                <span>Origin</span>
                <span>Dest</span>
                <span>Equip</span>
                <span>Rate</span>
                <span>Pickup</span>
              </div>

              <div className="rounded-lg border border-border bg-card">
                {matches.map((load) => (
                  <LoadRow key={load.id} load={load} />
                ))}
              </div>

              {matchCount > 5 && (
                <p className="text-[10px] text-muted-foreground text-center">
                  +{matchCount - 5} more loads available in Search Loads
                </p>
              )}
            </>
          )}

          {/* Ready with no matches */}
          {status === "ready" && matchCount === 0 && (
            <div className="flex items-center gap-2 px-2 py-3 rounded-lg bg-muted/50 border border-border text-xs text-muted-foreground">
              <Package className="h-4 w-4" />
              <span>No matching loads found for this lane right now.</span>
            </div>
          )}

          {/* Error */}
          {status === "error" && (
            <div className="flex items-center gap-2 px-2 py-3 rounded-lg bg-muted/50 border border-border text-xs text-muted-foreground">
              <AlertCircle className="h-4 w-4" />
              <span>Unable to load matches. Your truck was posted successfully.</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} className="text-xs">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
